import { z } from 'zod';
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ONE RULE FOR A DEADLINE
 *
 * A due date may be today. It may not be yesterday.
 *
 * The product had already decided this — the bulk importer rejected a past
 * date with "That date has already passed" — but only the bulk importer
 * enforced it. The same assignment made through either of the two on-screen
 * pickers was accepted with no complaint, so the identical action was an error
 * via Excel and fine via the UI.
 *
 * What the screens had instead was `min` on the date input, which is not
 * enforcement: neither picker sits in a form, so nothing validates on submit.
 * It greys out earlier days and that is all. One of the two did not even carry
 * it, and the one that did computed it with `toISOString().slice(0, 10)` —
 * which in IST names YESTERDAY between midnight and half past five, so for
 * five and a half hours of every day the guard permitted exactly the date it
 * existed to forbid.
 *
 * A back-dated deadline is not a harmless typo. The assignment lands already
 * overdue, on a compliance count the learner cannot clear, and the reminder
 * sweep only ever looks forward — so the one reminder that would explain it
 * never arrives.
 *
 * Hence this file, and hence four call sites importing from it rather than
 * four spellings of the same sentence.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const DUE_DATE_PAST_MESSAGE = 'That date has already passed';
/**
 * Whether a deadline falls before today.
 *
 * Compared as CALENDAR DAYS, not as instants, and that distinction is the
 * whole of the care here.
 *
 * A date input sends `2026-09-14`, and a bare `YYYY-MM-DD` parses as UTC
 * midnight. Comparing that instant against a server's local start-of-day
 * happens to work east of UTC and silently fails west of it, where UTC
 * midnight lands BEFORE the local day began — rejecting today as past. So both
 * sides are reduced to a day number first: the submitted value by its UTC
 * parts, which is the day the sender typed, and "today" by the server's local
 * parts, which is the day it currently is here.
 */
export function isPastDueDate(value, now = new Date()) {
    const submittedDay = Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
    const currentDay = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return submittedDay < currentDay;
}
/**
 * An optional deadline that cannot already have passed.
 *
 * `.optional().nullable()` stays outermost so an absent or explicitly null due
 * date short-circuits before the check — "no deadline" is a normal answer and
 * must never be told it has passed.
 */
export const dueDateSchema = z.coerce
    .date()
    .refine((value) => !isPastDueDate(value), { message: DUE_DATE_PAST_MESSAGE })
    .optional()
    .nullable();
//# sourceMappingURL=dueDate.js.map